import { queryClient } from "@istari-frontend/api-client";
import { type InfiniteData, useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  appendConversationMessage,
  type ConversationPage,
  createConversation,
  listConversationMessages,
  listConversations,
  renameConversation,
  setConversationArchived,
} from "~/ai-chat/data-source/conversations-api";
import type { Conversation, ConversationMessage } from "~/ai-chat/model/conversation";

interface ConversationListParams {
  q?: string;
  includeArchived?: boolean;
}

export const conversationKeys = {
  all: ["ai-conversations"] as const,
  lists: () => [...conversationKeys.all, "list"] as const,
  list: ({ q = "", includeArchived = false }: ConversationListParams = {}) =>
    [...conversationKeys.lists(), q, includeArchived] as const,
  messages: (conversationId: string) => [...conversationKeys.all, conversationId, "messages"] as const,
};

const CONVERSATIONS_PAGE_SIZE = 30;

export function useConversations(params: ConversationListParams = {}) {
  const q = params.q?.trim() ?? "";
  const includeArchived = params.includeArchived ?? false;
  return useInfiniteQuery({
    queryKey: conversationKeys.list({ q, includeArchived }),
    queryFn: ({ pageParam }) =>
      listConversations({ q, includeArchived, cursor: pageParam, size: CONVERSATIONS_PAGE_SIZE }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });
}

// Flatten the infinite pages into a single list for rendering.
export function flattenConversations(data: InfiniteData<ConversationPage> | undefined): Conversation[] {
  return data?.pages.flatMap((page) => page.items) ?? [];
}

// Apply an update across every cached list variant (search terms / archived
// filter each get their own cache entry) so an optimistic edit isn't stranded.
function patchCachedConversation(conversationId: string, patch: (conversation: Conversation) => Conversation) {
  queryClient.setQueriesData<InfiniteData<ConversationPage>>({ queryKey: conversationKeys.lists() }, (data) =>
    data === undefined
      ? data
      : {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            items: page.items.map((conversation) =>
              conversation.id === conversationId ? patch(conversation) : conversation,
            ),
          })),
        },
  );
}

export function useCreateConversation() {
  return useMutation({
    mutationFn: (title: string | null) => createConversation(title),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

type RenameConversationParams = {
  conversationId: string;
  title: string | null;
};

export function useRenameConversation() {
  return useMutation({
    mutationFn: ({ conversationId, title }: RenameConversationParams) => renameConversation(conversationId, title),
    onMutate: async ({ conversationId, title }) => {
      await queryClient.cancelQueries({ queryKey: conversationKeys.lists() });
      const previous = queryClient.getQueriesData<InfiniteData<ConversationPage>>({
        queryKey: conversationKeys.lists(),
      });
      patchCachedConversation(conversationId, (conversation) => ({ ...conversation, title }));
      return { previous };
    },
    onError: (_error, _params, context) => {
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

type ArchiveConversationParams = {
  conversationId: string;
  archived: boolean;
};

export function useArchiveConversation() {
  return useMutation({
    mutationFn: ({ conversationId, archived }: ArchiveConversationParams) =>
      setConversationArchived(conversationId, archived),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
    // Without feedback the archive action looks like it did nothing when the request fails.
    onError: (_error, { archived }) => {
      toast.error(archived ? "Couldn't archive the conversation." : "Couldn't unarchive the conversation.");
    },
  });
}

export function useConversationMessages(conversationId: string | null) {
  return useQuery({
    queryKey: conversationKeys.messages(conversationId ?? ""),
    queryFn: () => listConversationMessages(conversationId ?? ""),
    enabled: conversationId !== null,
  });
}

type AppendMessageParams = {
  conversationId: string;
  role: ConversationMessage["role"];
  content: string;
};

export function useAppendConversationMessage() {
  return useMutation({
    mutationFn: ({ conversationId, role, content }: AppendMessageParams) =>
      appendConversationMessage(conversationId, role, content),
    onSuccess: (_message, { conversationId }) => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.messages(conversationId) });
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}
